import { useContext, useState } from "react";
import { deleteData, OAuthError, postData } from "../backend";
import { TrashIcon, ChevronRightIcon, UserCircleIcon, XIcon, ChevronDownIcon, InformationCircleIcon } from "@heroicons/react/outline";
import { Document, SignatureField, SignatureConfig } from "../interfaces";
import { Combobox } from "@headlessui/react";
import { WorkflowContext, useModal } from "../contexts";
import { SourceField } from "../interfaces";
import { ORDINAL_MAP, RECIPIENT_COLOUR_MAP } from "../utils";
import OAuthModalHubspot from "./OAuthModalHubspot";
import OAuthModalXero from "./OAuthModalXero";
import OAuthModal from "./OAuthModal";
import ErrorModal from "./ErrorModal";
import { useTranslation } from "react-i18next";
import WorkflowSignatureFieldsOverlay from "./WorkflowSignatureFieldsOverlay";
import WorkflowSignatureFieldsInline from "./WorkflowSignatureFieldsInline";

interface Props {
  document?: Document;
  recipientNumber: number;
  signatureConfig: SignatureConfig
  updateSignatureConfig: (updates: any) => void;
  hideFields?: boolean;
  allowRemove?: boolean;
  insertSignatureField?: (field: SignatureField, recipient: number, required: boolean) => void;
}

function WorkflowSignatureConfigItem(props: Props) {
  const { document, recipientNumber, signatureConfig, updateSignatureConfig } = props;
  const { t } = useTranslation();

  const workflowContext = useContext(WorkflowContext);
  const { workflow } = workflowContext;

  const { openModal } = useModal();

  const recipientEmailKey = "recipientEmail" + recipientNumber;
  const [textColor, bgColor] = RECIPIENT_COLOUR_MAP[recipientNumber];

  // @ts-ignore
  const [email, setEmail] = useState<string>(signatureConfig[recipientEmailKey]);

  const [isOpened, setIsOpened] = useState(!!document); // Only open when there is a document present
  const [clickedField, setClickedField] = useState<SignatureField | "TEXT_REQUIRED" | "CHECKBOX_REQUIRED">();

  async function handleInsertSignatureField(field: SignatureField, recipient: number, required: boolean = false) {
    if (clickedField)
      return; // Prevent action while a field is already being inserted

    if (props.insertSignatureField)
      return props.insertSignatureField(field, recipient, required);

    const type = field.toLocaleLowerCase();

    try {
      setClickedField(field + (required ? "_REQUIRED" : "") as SignatureField);
      await postData(`/workflows/documents/${document!.id}/add-signature-field/`, { type, recipient, required })
    } catch (error) {
      if (error instanceof OAuthError)
        if (error.oauthUrl.includes("app.hubspot"))
          openModal(<OAuthModalHubspot oauthUrl={error.oauthUrl} />);
        else if (error.oauthUrl.includes("xero"))
          openModal(<OAuthModalXero oauthUrl={error.oauthUrl} />);
        else
          openModal(<OAuthModal oauthUrl={error.oauthUrl} />);
      else
        openModal(<ErrorModal details={error} />);
    } finally {
      setClickedField(undefined)
    }
  }

  function handleUpdateEmail(newEmail?: string) {
    setEmail(newEmail ?? email);
    updateSignatureConfig({ [recipientEmailKey]: newEmail ?? email });
  }

  async function handleRemoveRecipient() {
    // Remove any email override so it isn't reapplied if the recipient is added again
    const override = signatureConfig.overrides?.find(o => o.recipientNumber === recipientNumber);
    if (override)
      await deleteData(`/signatures/signature-config-overrides/${override.id}/`).catch(console.error);

    const numRecipients = Math.max(signatureConfig!.numRecipients - 1, 1);
    updateSignatureConfig({ numRecipients, [recipientEmailKey]: "" });
  }

  const filteredContacts = workflow.source?.hubspotConfig?.hubspotContactLabels.filter((label: string) => label.toLowerCase().includes(email.toLowerCase())) ?? [];
  const filteredFields = workflow.source?.sourceFields.filter((sourceField: SourceField) => {
    return sourceField.fieldName.toLowerCase().includes(email.toLowerCase().replaceAll("{", "").replaceAll("}", ""))
  }) ?? [];

  if (!workflow?.signatureConfig)
    return null

  const hubspotContactLabel = email.match(/{{__hscontact_(.+?)(?::(.+))?}}/);

  return (
    <div className="border border-gray-400 rounded">
      <div className={`flex flex-col gap-1 border-b px-2 py-1 pb-2 bg-opacity-10 rounded ${bgColor}`}>
        <div className="flex items-center w-full">
          <div className={`flex items-center cursor-pointer ${textColor}`} onClick={() => setIsOpened(f => !f)}>
            {(!!document && false) &&
              <ChevronRightIcon className="w-6 h-6 mr-2" style={{ "rotate": isOpened ? "90deg" : "0deg" }} />
            }
            <div className={`font-gilroy font-semibold text-sm`}>
              {ORDINAL_MAP[recipientNumber]} {t("Recipient")}
            </div>
          </div>
          {((document || props.allowRemove) && recipientNumber > 1 && recipientNumber === signatureConfig.numRecipients) &&
            <TrashIcon className="w-4 h-4 ml-auto text-gray-600 hover:text-red cursor-pointer" onClick={handleRemoveRecipient} />
          }
          {(document && recipientNumber === 1) &&
            <div className="relative group ml-auto">
              <InformationCircleIcon className="w-4 h-4 ml-auto text-gray-600" />
              <div className="tooltip group-hover:visible right-2 top-4 text-wrap w-60 rounded-tr-none">
                {t("Signable fields will be inserted at the")} <b className="whitespace-nowrap">{t("end of the document")}</b> {t("and can be moved to any location.")}
              </div>
            </div>
          }
        </div>
        <div className="relative w-full">
          <Combobox value={email} onChange={handleUpdateEmail}>
            {hubspotContactLabel
              ? (
                <div className="border-2 rounded py-1 px-2 w-full outline-none text-sm flex items-center bg-white">
                  <UserCircleIcon className="h-4 mr-2 text-[#6e839a]" />
                  <div className="text-sm font-semibold text-gray-600">{hubspotContactLabel[2] || t("Contact")}</div>
                  <Combobox.Button className="absolute right-0 top-0 bottom-0 flex items-center pr-2" onClick={() => handleUpdateEmail("")}>
                    <XIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                  </Combobox.Button>
                </div>
              ) : (
                <div className="relative">
                  <Combobox.Input className="border-2 rounded py-1 px-2 w-full pr-8 outline-none text-sm" placeholder={t("Enter or select recipient email")} onChange={(event) => setEmail(event.target.value)} />
                  <Combobox.Button className="absolute right-0 top-0 bottom-0 flex items-center pr-2" onClick={() => handleUpdateEmail("")}>
                    {email.length === 0
                      ? <ChevronDownIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                      : <XIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                    }
                  </Combobox.Button>
                </div>
              )
            }
            <Combobox.Options className="absolute top-9 w-full z-40 flex flex-col max-h-52 bg-white border rounded shadow overflow-y-auto">
              <>
                {!!workflow.source?.hubspotConfig &&
                  <>
                    {email.length === 0 &&
                      <Combobox.Option value={`{{__hscontact_${recipientNumber}}}`} className="flex items-center text-sm text-left border-b border-gray-200 last:border-none p-2 hover:bg-blue hover:bg-opacity-10 cursor-pointer group flex-shrink-0">
                        <UserCircleIcon className="h-8 mr-2 text-[#6e839a]" />
                        <div className="flex flex-col items-start">
                          <div className="text-xs font-semibold text-gray-600">{t("HubSpot Contact")}</div>
                        </div>
                      </Combobox.Option>
                    }
                    {filteredContacts.map((label) => (
                      <Combobox.Option key={label} value={`{{__hscontact_${label}}}`} className="flex items-center text-sm text-left border-b border-gray-200 last:border-none p-2 hover:bg-blue hover:bg-opacity-10 cursor-pointer group flex-shrink-0">
                        <UserCircleIcon className="h-8 mr-2 text-[#6e839a]" />
                        <div className="flex flex-col items-start">
                          <div className="text-xs font-semibold text-gray-400">{t("HubSpot Contact")}</div>
                          <div className="flex items-center gap-1 text-sm font-semibold font-gilroy text-gray-600">
                            {t("Labeled")}:
                            <div className="text-sm bg-gray-100 rounded px-2 font-semibold font-gilroy text-gray-600">
                              {label.split(":")[1]}
                            </div>
                          </div>
                        </div>
                      </Combobox.Option>
                    ))}
                  </>
                }
                {!workflow.source?.hubspotConfig &&
                  <>
                    {filteredFields.map((field) => (
                      <Combobox.Option key={field.id} value={`{{${field.fieldName}}}`} className="flex items-center justify-between text-left text-sm border-b border-gray-200 last:border-none px-2 pt-2 pb-1 hover:bg-blue hover:bg-opacity-10 cursor-pointer group flex-shrink-0">
                        {field.fieldName}
                      </Combobox.Option>
                    ))}
                  </>
                }
                {email.length > 0 &&
                  <Combobox.Option value={email} className="flex items-center justify-between text-left text-sm border-b text-gray-400 border-gray-200 last:border-none px-2 pt-2 pb-1 hover:bg-blue hover:bg-opacity-10 cursor-pointer group flex-shrink-0">
                    {email}
                    <div className="hidden group-first:flex text-xs bg-blue-50 roudned px-1 font-semibold font-gilroy text-gray-600">
                      Enter
                    </div>
                  </Combobox.Option>
                }
              </>
            </Combobox.Options>
          </Combobox>
        </div>
      </div>

      {(document?.documentType === "PDF_FILLABLE" && document && isOpened && !props.hideFields) &&
        <WorkflowSignatureFieldsOverlay recipientNumber={recipientNumber} signatureConfig={signatureConfig} insertSignatureField={handleInsertSignatureField} />
      }

      {(document?.documentType !== "PDF_FILLABLE" && document && isOpened && !props.hideFields) &&
        <WorkflowSignatureFieldsInline recipientNumber={recipientNumber} signatureConfig={signatureConfig} />
      }
    </div>
  );
}

export default WorkflowSignatureConfigItem;
